import GroupAdd from '@mui/icons-material/GroupAdd'
import Settings from '@mui/icons-material/Settings'
import InputIcon from '@mui/icons-material/Input'
import Delete from '@mui/icons-material/Delete'
import CallSplit from '@mui/icons-material/CallSplit'
import Stop from '@mui/icons-material/Stop'
import PlayArrow from '@mui/icons-material/PlayArrow'
import Box from '@mui/material/Box'
import { useRoutes, type AppDispatch } from '../../../store'
import { EnrichedInput } from '../../../api/nm-types'
import { ExperimentalFeatures, InputAdminStatus, User } from 'common/api/v1/types'
import { DraftActions, draftInputs, DraftSources, enableInputs } from '../../../redux/actions/inputsActions'
import { canEditInput } from './index'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { ActionMenu as BaseActionMenu } from '../../common/Table/ActionMenu'
import { ServiceOverviewButton } from '../../serviceOverview/Overview/index'

interface ActionMenuProps {
  input: EnrichedInput
  user: User
  dispatch: AppDispatch
  experimentalFeatures?: ExperimentalFeatures
}

export const ActionMenu = (props: ActionMenuProps) => {
  const { input, dispatch, user, experimentalFeatures } = props
  const isOwner = canEditInput(user, input.owner)
  const routes = useRoutes()

  return (
    <Box sx={{ display: 'flex' }}>
      <ServiceOverviewButton input={input} />
      <BaseActionMenu
        data-test-id="input-action-menu"
        options={[
          ...(isOwner
            ? [
                {
                  label: 'Edit',
                  icon: <Settings />,
                  href: routes.inputsUpdate({ id: input.id }),
                  'data-test-id': 'edit-input',
                },
                {
                  label: 'Share',
                  icon: <GroupAdd />,
                  onClick: () => {
                    dispatch(
                      draftInputs({ inputs: [input], action: DraftActions.share, source: DraftSources.actionMenu }),
                    )
                  },
                  'data-test-id': 'share-input',
                },
                {
                  label: input.adminStatus === InputAdminStatus.on ? 'Disable' : 'Enable',
                  icon: input.adminStatus === InputAdminStatus.on ? <Stop /> : <PlayArrow />,
                  onClick: () => {
                    if (input.adminStatus === InputAdminStatus.on) {
                      dispatch(
                        draftInputs({ action: DraftActions.disable, inputs: [input], source: DraftSources.actionMenu }),
                      )
                    } else {
                      dispatch(enableInputs([input.id]))
                      dispatch(draftInputs({ inputs: [] }))
                    }
                  },
                  'data-test-id': 'toggle-admin-status-input',
                },
              ]
            : []),
          {
            label: 'Send to output',
            icon: <InputIcon />,
            onClick: () => {
              dispatch(draftInputs({ inputs: [input], action: DraftActions.send, source: DraftSources.actionMenu }))
            },
            'data-test-id': 'send-to-output',
          },
          ...(experimentalFeatures && experimentalFeatures.ExtDerivedInput && !input.deriveFrom && isOwner
            ? [
                {
                  label: 'Transform',
                  icon: <CallSplit />,
                  href: routes.inputsDerive({ id: input.id }),
                  'data-test-id': 'derive-from',
                },
              ]
            : []),
          ...(isOwner
            ? [
                {
                  label: 'Duplicate',
                  icon: <ContentCopyIcon />,
                  href: routes.inputsCopy({ id: input.id }),
                  'data-test-id': 'use-input-as-template',
                },
                {
                  label: 'Delete',
                  icon: <Delete />,
                  isDestructive: true,
                  onClick: () => {
                    dispatch(
                      draftInputs({ inputs: [input], action: DraftActions.delete, source: DraftSources.actionMenu }),
                    )
                  },
                  'data-test-id': 'delete-input',
                },
              ]
            : []),
        ]}
      />
    </Box>
  )
}
